// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContext, PerunAppState } from "../AppContext";
import { Client } from "../client/Client";
import { Spinner } from "react-bootstrap";
import { initWeb3, onboardFr } from "../web3/web3";
import { CkbCCCProvider, Web3Provider } from "../client/Provider";
import { newCKBClient } from "../constants/CKBNetwork";
import { ClientJsonRpc } from "@ckb-ccc/core";
import { Connection } from "../wire/connection";
import { CKBContext } from "./CKBProvider";
import { default as config } from "../config.json";
import { Page } from "../App";
import Address from "../api/types/address";
import ProposalID from "../api/types/poposalID";
import Swap from "../channel/Swap";
import { AnyAsset, deserializeAssets } from "../api/types/nervosAsset";

/**
 * If a swap was in progress when the page was last closed (swapStage === 2),
 * reconstruct the Swap object and add it to the client's state. This ensures
 * that when the backend sends a `channelCreated` event on reconnect, the
 * client can match it to the swap and call performSwap correctly.
 */
function tryRestoreCurrentSwap(clientInstance: Client) {
  try {
    const stored = localStorage.getItem("currentSwap");
    if (!stored) return;

    const parsed = JSON.parse(stored);
    // Only restore if the user was actively performing a swap.
    if (
      parsed.swapStage !== 2 ||
      !parsed.swapID ||
      !parsed.swapPair ||
      !parsed.swapAmount
    )
      return;

    const assets = deserializeAssets(parsed.swapPair) as [AnyAsset, AnyAsset];
    if (assets.length !== 2) return;

    const balances: bigint[] = [
      BigInt(parsed.swapAmount[0]),
      BigInt(parsed.swapAmount[1]),
    ];
    const proposalID = ProposalID.fromJSON(parsed.swapID);

    let peerAddressEth = Address.zero();
    let peerAddressCC = "";
    const fromHub = parsed.fromHub;
    const toHub = parsed.toHub;
    if (toHub?.backendID) {
      peerAddressEth = Address.fromJSON(toHub.address);
      if (!fromHub?.backendID) peerAddressCC = fromHub?.address ?? "";
    } else if (toHub) {
      peerAddressCC = toHub.address;
      if (fromHub?.backendID)
        peerAddressEth = Address.fromJSON(fromHub.address);
    }

    const swap = new Swap(
      proposalID,
      peerAddressEth,
      peerAddressCC,
      assets,
      balances,
      parsed.receiverAddr,
    );
    clientInstance.state.addSwap(swap);
    console.log("Navigator: restored in-progress swap", proposalID.toString());
  } catch (err) {
    console.warn("Navigator: could not restore in-progress swap:", err);
  }
}

interface NavigatorProps {
  setPage: (page: Page) => void;
}

export function Navigator(props: NavigatorProps) {
  const ctx = useContext(AppContext);
  const { signer, open } = useContext(CKBContext);

  const [loading, setLoading] = useState(true);
  const [lastPage, setLastPage] = useState<Page | null>(null);
  const [reconnected, setReconnected] = useState(false);
  const reconnectStarted = useRef(false);

  useEffect(() => {
    const tryInit = async () => {
      const stored = localStorage.getItem("clientState");
      if (!stored) return;

      const state = JSON.parse(stored);

      if (state.nervosAddress && !signer) {
        console.log("Opening CCC wallet ONCE...");
        await open();
      }
    };

    tryInit();
  }, []);

  useEffect(() => {
    if (!signer || reconnected || reconnectStarted.current) return;
    reconnectStarted.current = true;
    const reconnect = async () => {
      console.log("Navigator: Attempting to reconnect client...");
      const stored = localStorage.getItem("clientState");
      if (!stored) {
        console.log("No client state found, redirecting to onboarding.");
        props.setPage("ONBOARDING");
        setLoading(false);
        return;
      }

      try {
        console.log(
          "Client state found, parsing and initializing providers...",
          stored,
        );
        const state = JSON.parse(stored);
        if (!state.ethAddress && !state.steAddress && !state.nervosAddress) {
          console.log(
            "Client state missing addresses, redirecting to onboarding.",
          );
          props.setPage("ONBOARDING");
          setLoading(false);
          return;
        }

        let ethProvider, steProvider, nervosProvider;

        // --- Ethereum ---
        if (state.ethAddress) {
          state.ethAddress = Address.fromJSON(state.ethAddress);
          console.log(
            "Ethereum address found in state, initializing Web3 provider...",
          );
          const mmProvider = await initWeb3();
          if (mmProvider) {
            // The cached session and the wallet's currently selected account
            // are two different things, and restoring one under the other
            // impersonates it: the page then shows the cached identity's
            // balance while every transaction the wallet signs matches none
            // of that identity's inputs, so CCC silently signs nothing and
            // the backend submits unsigned transactions (mainnet 2026-09-01,
            // OmniLock error 8 on funding). Refuse to restore on a mismatch;
            // the cached state is kept for when the right account returns.
            const accounts = await mmProvider.listAccounts();
            const active = accounts[0];
            if (
              active &&
              active.toLowerCase() !== state.ethAddress.toString().toLowerCase()
            ) {
              console.error(
                `Navigator: cached session belongs to ${state.ethAddress.toString()} ` +
                  `but the wallet has ${active} selected — not restoring it.`,
              );
              props.setPage("ONBOARDING");
              setLoading(false);
              return;
            }
            ethProvider = new Web3Provider(
              mmProvider,
              state.ethAddress.toString(),
            );
          }
        }

        // --- Stellar ---
        if (state.steAddress) {
          console.log(
            "Stellar address found in state, initializing Stellar provider...",
          );
          const res = await onboardFr("TESTNET");
          steProvider = res.provider;
        }

        // --- Nervos ---
        if (state.nervosAddress) {
          console.log(
            "Nervos address found in state, initializing CKB provider...",
          );
          const client: ClientJsonRpc = newCKBClient();
          // Force the signer to use the correct network client. On page reload
          // the CCC connector auto-reconnects before CKBProvider.setClient()
          // fires, so the signer's internal client defaults to ClientPublicTestnet.
          // Without this, signTransaction queries the wrong node for cells.
          (signer as any).client_ = client;
          nervosProvider = new CkbCCCProvider(signer, client, state.mmAccount);
        }

        // --- WebSocket ---
        const savedCounter = parseInt(
          localStorage.getItem("wsRequestCounter") || "0",
          10,
        );
        const conn = new Connection(
          new URL(`${config.wsAddress}:${config.wsPort + config.wsPath}`),
          savedCounter,
        );

        const clientInstance = new Client(
          conn,
          ethProvider,
          steProvider,
          nervosProvider,
          Address.toJSON(state.ethAddress),
          state.stellarAddress,
          state.nervosAddress,
          state.nervosPubKey,
        );

        // Pre-load any in-progress swap so channelCreated events can be matched.
        tryRestoreCurrentSwap(clientInstance);

        console.log("Client instance created, initializing connection...");
        const success = await clientInstance.initializeConn(state.l2Account);
        if (!success) throw new Error("Failed to initialize client connection");

        // Persist the (possibly refreshed) l2Account for the next reload.
        if (clientInstance.l2Address) {
          const updatedState = JSON.parse(stored);
          updatedState.l2Account = Address.toJSON(clientInstance.l2Address);
          localStorage.setItem("clientState", JSON.stringify(updatedState));
        }

        // Set the client, but don't switch page yet
        ctx.setClient(clientInstance);

        // Save last page to switch after client is ready
        setLastPage((localStorage.getItem("site") as Page) || "ONBOARDING");
        setReconnected(true);
      } catch (err) {
        console.error("Navigator failed to reconnect client: ", err);
        props.setPage("ONBOARDING");
      } finally {
        setLoading(false);
      }
    };
    reconnect();
  }, [signer, reconnected]);

  // Wait for client to exist before setting page
  useEffect(() => {
    if (ctx.client && lastPage) {
      console.log("Client is ready, setting page to:", lastPage);
      props.setPage(lastPage);
      setLastPage(null); // reset so we don't trigger again
    }
  }, [ctx.client, lastPage]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" /> Connecting...
      </div>
    );
  }

  return <></>;
}
